import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Table, Select,
  Button, Space, Spin, Empty, Tag
} from 'antd';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend
} from 'recharts';
import {
  DollarOutlined, UserOutlined, TeamOutlined,
  RiseOutlined, ReloadOutlined, HeartOutlined,
  CalendarOutlined, SafetyCertificateOutlined,
  ClockCircleOutlined, UserAddOutlined, SolutionOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

const AnalyticsDashboard: React.FC = () => {
  const [loading, setLoading]           = useState(false);
  const [dashboardData, setDashboardData] = useState<any>({});
  const [departmentData, setDepartmentData] = useState<any[]>([]);
  const [salaryData, setSalaryData]       = useState<any[]>([]);
  const [trends, setTrends]               = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');

  useEffect(() => { fetchAll(); }, [selectedPeriod]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const base = `/backend/hr-api/analytics/analytics.php?tenant_id=${apiService.getTenantId()}&period=${selectedPeriod}`;
      const [d, dept, sal, tr] = await Promise.all([
        apiService.getAnalyticsDashboard(),
        fetch(`https://nexus-hr.talenthubupskilling.com${base}&action=department_breakdown`).then(r => r.json()),
        fetch(`https://nexus-hr.talenthubupskilling.com${base}&action=salary_analysis`).then(r => r.json()),
        fetch(`https://nexus-hr.talenthubupskilling.com${base}&action=payroll_trends`).then(r => r.json()),
      ]);
      if (d.success)    setDashboardData(d.data || {});
      if (dept.success) setDepartmentData(dept.data || []);
      if (sal.success)  setSalaryData(sal.data || []);
      if (tr.success)   setTrends(tr.data || []);
    } catch (e) {
      console.error('Analytics error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v: any) => `₦${Number(v || 0).toLocaleString()}`;

  const departmentColumns = [
    { title: 'Department',    dataIndex: 'department',    key: 'department' },
    { title: 'Employees',     dataIndex: 'employee_count',key: 'employee_count' },
    { title: 'Total Gross',   dataIndex: 'total_gross',   key: 'total_gross',   render: fmt },
    { title: 'Total Net',     dataIndex: 'total_net',     key: 'total_net',     render: fmt },
    { title: 'Avg Salary',    dataIndex: 'avg_salary',    key: 'avg_salary',    render: fmt },
  ];

  const salaryColumns = [
    { title: 'Employee',    dataIndex: 'employee_name',   key: 'employee_name', render: (v:string) => v || 'N/A' },
    { title: 'Department',  dataIndex: 'department',      key: 'department',    render: (v:string) => v || 'N/A' },
    { title: 'Basic Salary',dataIndex: 'basic_salary',    key: 'basic_salary',  render: fmt },
    { title: 'Gross Pay',   dataIndex: 'gross_pay',       key: 'gross_pay',     render: (v:any) => v ? fmt(v) : <Tag color="orange">No payroll</Tag> },
    { title: 'Net Pay',     dataIndex: 'net_pay',         key: 'net_pay',       render: (v:any) => v ? fmt(v) : '-' },
    { title: 'Status',      dataIndex: 'status',          key: 'status',
      render: (s:string) => (
        <Tag color={s === 'processed' || s === 'paid' ? 'green' : s === 'approved' ? 'blue' : 'orange'}>
          {(s || 'no payroll').toUpperCase()}
        </Tag>
      )
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <h1 style={{ margin:0 }}>Analytics & Reports</h1>
        <Space>
          <Select value={selectedPeriod} onChange={setSelectedPeriod} style={{ width:180 }}>
            <Select.Option value="current_month">Current Month</Select.Option>
            <Select.Option value="last_month">Last Month</Select.Option>
            <Select.Option value="quarter">This Quarter</Select.Option>
            <Select.Option value="year">This Year</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchAll}>Refresh</Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* KPI Cards */}
        <Row gutter={[16,16]} style={{ marginBottom:24 }}>
          {[
            { title:'Total Employees',  value: dashboardData.total_employees,  icon:<UserOutlined />,    color:'#1890ff' },
            { title:'Monthly Payroll',  value: dashboardData.monthly_payroll,  icon:<DollarOutlined />,  color:'#52c41a', prefix:'₦' },
            { title:'Average Salary',   value: dashboardData.avg_salary,       icon:<RiseOutlined />,    color:'#722ed1', prefix:'₦' },
            { title:'Departments',      value: dashboardData.total_departments, icon:<TeamOutlined />,    color:'#faad14' },
            { title:'Active Benefits',  value: dashboardData.active_benefits,  icon:<HeartOutlined />,   color:'#eb2f96' },
            { title:'Pending Leaves',   value: dashboardData.pending_leaves,   icon:<CalendarOutlined />,color:'#fa8c16' },
            { title:'Retention Rate',   value: dashboardData.retention_rate,   icon:<SafetyCertificateOutlined />, color:'#13c2c2', suffix:'%' },
            { title:'Avg. Time to Hire',value: dashboardData.avg_time_to_hire, icon:<ClockCircleOutlined />, color:'#fa541c', suffix:' days' },
            { title:'New Hires (Month)',value: dashboardData.new_hires_this_month, icon:<UserAddOutlined />, color:'#52c41a' },
            { title:'Open Positions',   value: dashboardData.open_positions,   icon:<SolutionOutlined />, color:'#1890ff' },
          ].map((s,i) => (
            <Col xs={24} sm={12} lg={4} key={i}>
              <Card size="small">
                <Statistic
                  title={s.title}
                  value={s.value === null || s.value === undefined ? 'N/A' : Number(s.value).toLocaleString()}
                  prefix={s.icon}
                  suffix={(s.value !== null && s.value !== undefined) ? (s as any).suffix : ''}
                  valueStyle={{ color: s.color, fontSize:20 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts row 1 */}
        <Row gutter={[16,16]} style={{ marginBottom:24 }}>
          <Col xs={24} lg={14}>
            <Card title="Department Payroll Distribution">
              {departmentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={departmentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" />
                    <YAxis tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v:any) => fmt(v)} />
                    <Legend />
                    <Bar dataKey="total_gross" name="Gross Pay" fill="#1890ff" />
                    <Bar dataKey="total_net"   name="Net Pay"   fill="#52c41a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty description="No payroll data for this period" />}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title="Employee Distribution by Department">
              {departmentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={departmentData}
                      cx="50%" cy="50%" outerRadius={100}
                      dataKey="employee_count"
                      nameKey="department"
                      label={(p:any) => `${p.department}: ${p.employee_count}`}
                    >
                      {departmentData.map((_,i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty description="No department data" />}
            </Card>
          </Col>
        </Row>

        {/* Payroll trends */}
        {trends.length > 0 && (
          <Row gutter={[16,16]} style={{ marginBottom:24 }}>
            <Col span={24}>
              <Card title="Payroll Trends (Last 12 Months)">
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={[...trends].reverse()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v:any) => fmt(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="total_net"   name="Net Pay"   stroke="#1890ff" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="total_gross" name="Gross Pay" stroke="#52c41a" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Tables */}
        <Row gutter={[16,16]}>
          <Col xs={24} lg={12}>
            <Card title="Department Analysis">
              <Table
                columns={departmentColumns}
                dataSource={departmentData}
                pagination={false}
                size="small"
                rowKey="department"
                locale={{ emptyText: 'No department data' }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Employee Salary Analysis">
              <Table
                columns={salaryColumns}
                dataSource={salaryData}
                pagination={{ pageSize:5 }}
                size="small"
                rowKey="employee_name"
                locale={{ emptyText: 'No employee data' }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default AnalyticsDashboard;
